import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ThunderboltOutlined, ArrowRightOutlined, RobotOutlined } from '@ant-design/icons';
import {
  PORTFOLIO_SHOWCASE_SCENARIOS,
  CAT_COLORS,
  fmtInr,
  fmtInrShort,
  fmtPct,
} from './portfolioShowcaseData';
import './PortfolioBuilderShowcase.css';

const CYCLE_MS = 7000;
const BUILD_MS = 900;

const ease = [0.16, 1, 0.3, 1];

const slideVariants = {
  enter: (dir) => ({ opacity: 0, x: dir > 0 ? 28 : -28, filter: 'blur(6px)' }),
  center: { opacity: 1, x: 0, filter: 'blur(0px)' },
  exit: (dir) => ({ opacity: 0, x: dir > 0 ? -28 : 28, filter: 'blur(6px)' }),
};

const INPUT_FIELDS = [
  { key: 'risk_profile', label: 'Risk', full: true },
  { key: 'investment_amount_inr', label: 'Investment', format: (v) => fmtInr(v) },
  { key: 'horizon', label: 'Horizon' },
  { key: 'rbi_rate_view', label: 'RBI view' },
  { key: 'tax_bracket', label: 'Tax' },
  { key: 'investor_type', label: 'Investor' },
];

function MetricCard({ label, value, accent, delay = 0 }) {
  return (
    <motion.div
      className="sb-fipb-metric"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, delay, ease }}
    >
      <span className="sb-fipb-metric-label">{label}</span>
      <span className={`sb-fipb-metric-value${accent ? ` sb-fipb-metric-${accent}` : ''}`}>{value}</span>
    </motion.div>
  );
}

function AllocationBar({ distribution }) {
  const sorted = Object.entries(distribution)
    .filter(([, v]) => v > 0)
    .sort(([, a], [, b]) => b - a);

  return (
    <div className="sb-fipb-alloc">
      <div className="sb-fipb-alloc-bar">
        {sorted.map(([cat, pct], i) => (
          <motion.div
            key={cat}
            className="sb-fipb-alloc-seg"
            style={{ background: CAT_COLORS[cat] || '#888' }}
            initial={{ flex: 0, opacity: 0 }}
            animate={{ flex: pct, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.15 + i * 0.06, ease }}
            title={`${cat}: ${pct.toFixed(1)}%`}
          />
        ))}
      </div>
      <div className="sb-fipb-alloc-legend">
        {sorted.map(([cat, pct], i) => (
          <motion.span
            key={cat}
            className="sb-fipb-leg-item"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.35 + i * 0.05, ease }}
          >
            <span className="sb-fipb-leg-dot" style={{ background: CAT_COLORS[cat] || '#888' }} />
            {cat} <strong>{pct.toFixed(0)}%</strong>
          </motion.span>
        ))}
      </div>
    </div>
  );
}

function PortfolioBuilderShowcase() {
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [isBuilding, setIsBuilding] = useState(false);
  const [paused, setPaused] = useState(false);

  const scenario = PORTFOLIO_SHOWCASE_SCENARIOS[activeIndex];

  const goTo = useCallback((idx) => {
    if (idx === activeIndex) return;
    setDirection(idx > activeIndex ? 1 : -1);
    setIsBuilding(true);
    setTimeout(() => {
      setActiveIndex(idx);
      setTimeout(() => setIsBuilding(false), BUILD_MS);
    }, 280);
  }, [activeIndex]);

  const goNext = useCallback(() => {
    const next = (activeIndex + 1) % PORTFOLIO_SHOWCASE_SCENARIOS.length;
    goTo(next);
  }, [activeIndex, goTo]);

  useEffect(() => {
    if (paused) return undefined;
    const timer = setInterval(goNext, CYCLE_MS);
    return () => clearInterval(timer);
  }, [goNext, paused]);

  const { inputs, metrics, holdings, ai_snippet, profile_label, profile_label_hindi, pillClass } = scenario;

  return (
    <div
      className="sb-fipb-showcase"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="sb-fipb-showcase-grid">
        {/* Left — inputs panel */}
        <div className="sb-fipb-inputs-panel">
          <div className="sb-fipb-panel-header">
            <ThunderboltOutlined className="sb-fipb-panel-icon" />
            <div>
              <div className="sb-fipb-panel-title">AI Portfolio Builder</div>
              <div className="sb-fipb-panel-sub">Rules-based · India debt universe</div>
            </div>
          </div>

          <div className="sb-fipb-persona-tabs" role="tablist" aria-label="Investor personas">
            {PORTFOLIO_SHOWCASE_SCENARIOS.map((s, idx) => (
              <button
                key={s.id}
                type="button"
                role="tab"
                aria-selected={idx === activeIndex}
                className={`sb-fipb-persona-tab${idx === activeIndex ? ' active' : ''}`}
                onClick={() => goTo(idx)}
              >
                {s.inputs.risk_profile.split(' — ')[1] || s.profile_label}
              </button>
            ))}
          </div>

          <div className="sb-fipb-inputs-label">Portfolio inputs</div>

          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={scenario.id}
              className="sb-fipb-inputs-list"
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.5, ease }}
            >
              {INPUT_FIELDS.map((field, i) => {
                const raw = inputs[field.key];
                const display = field.format ? field.format(raw) : raw;
                return (
                  <motion.div
                    key={field.key}
                    className={`sb-fipb-input-row${field.full ? ' sb-fipb-input-row--full' : ''}`}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: i * 0.05, ease }}
                  >
                    <span className="sb-fipb-input-key">{field.label}</span>
                    <span className="sb-fipb-input-val">{display}</span>
                  </motion.div>
                );
              })}
            </motion.div>
          </AnimatePresence>

          <motion.button
            type="button"
            className="sb-fipb-cta"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate('/fixed-income-portfolio')}
          >
            Build your portfolio
            <ArrowRightOutlined />
          </motion.button>
        </div>

        {/* Right — output card */}
        <div className="sb-fipb-output-panel">
          <AnimatePresence>
            {isBuilding && (
              <motion.div
                className="sb-fipb-building-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
              >
                <div className="sb-fipb-building-pulse" />
                <span className="sb-fipb-building-text">Optimising allocation…</span>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={scenario.id}
              className="sb-fipb-output-card"
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.55, ease }}
            >
              <div className="sb-fipb-output-header">
                <h3 className="sb-fipb-profile-name">
                  {profile_label}
                  <span className="sb-fipb-profile-hindi">{profile_label_hindi}</span>
                </h3>
                <span className={`sb-fipb-profile-pill ${pillClass}`}>
                  {inputs.risk_profile.split(' — ')[0]}
                </span>
              </div>

              <div className="sb-fipb-metrics-grid">
                <MetricCard label="Value" value={fmtInrShort(metrics.total_value_inr)} delay={0.05} />
                <MetricCard label="Yield" value={fmtPct(metrics.weighted_yield_pct)} accent="green" delay={0.1} />
                <MetricCard label="Income" value={fmtInr(metrics.annual_income_inr)} delay={0.15} />
                <MetricCard label="Duration" value={`${metrics.weighted_duration_years.toFixed(1)}y`} delay={0.2} />
              </div>

              <div className="sb-fipb-output-body">
                <div>
                  <div className="sb-fipb-section-label">Allocation</div>
                  <AllocationBar distribution={metrics.category_distribution} />
                </div>

                <div className="sb-fipb-holdings">
                  <div className="sb-fipb-section-label">Top holdings</div>
                  {holdings.slice(0, 2).map((h, i) => (
                    <motion.div
                      key={h.name}
                      className="sb-fipb-holding-row"
                      initial={{ opacity: 0, x: 16 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.45, delay: 0.4 + i * 0.08, ease }}
                    >
                      <div className="sb-fipb-holding-info">
                        <span className="sb-fipb-holding-name">{h.name}</span>
                        <span className="sb-fipb-holding-cat">{h.category}</span>
                      </div>
                      <div className="sb-fipb-holding-stats">
                        <span className="sb-fipb-holding-weight">{h.weight_pct.toFixed(0)}%</span>
                        <span className="sb-fipb-holding-yield">{h.indicative_yield.toFixed(2)}%</span>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <motion.div
                  className="sb-fipb-ai-snippet"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.55, delay: 0.55, ease }}
                >
                  <RobotOutlined className="sb-fipb-ai-icon" />
                  <div className="sb-fipb-ai-content">
                    <div className="sb-fipb-ai-header">
                      <span className="sb-fipb-ai-title">AI debt strategist</span>
                      <span className="sb-fipb-ai-badge">Narrative</span>
                    </div>
                    <p className="sb-fipb-ai-text">{ai_snippet}</p>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="sb-fipb-dots" role="tablist" aria-label="Portfolio scenarios">
            {PORTFOLIO_SHOWCASE_SCENARIOS.map((s, idx) => (
              <button
                key={s.id}
                type="button"
                className={`sb-fipb-dot${idx === activeIndex ? ' active' : ''}`}
                aria-label={`Show ${s.profile_label} scenario`}
                aria-selected={idx === activeIndex}
                onClick={() => goTo(idx)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PortfolioBuilderShowcase;
